import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { useCanUseProVoices } from '@/hooks/useCanUseProVoices';
import CloneVoice from '@/images/CloneVoice.svg';
import Import from '@/images/Import.svg';
import Plus from '@/images/plus.svg';
import theme from '@/theme';
import styled from '@emotion/styled';
import { MenuItem, Select, Tooltip } from '@mui/material';
import { Instance } from '@popperjs/core';
import mixpanel from 'mixpanel-browser';
import { useRef } from 'react';
import { styles, UpgradeNeededTooltipContent } from '../UpgradeNeededTooltipContent';

export const TransparentButton = styled(Button)<{ disabled?: boolean }>`
    background-color: transparent;
    border: 1px solid ${theme.colors.black[200]};
    height: 34px;
    font-size: 14px;
    padding: 7px 14px;

    ${({ disabled }) =>
        disabled &&
        `
         padding: 7px 14px 7px 30px;
    `}
    &.MuiButtonBase-root:hover {
        background-color: #f3f3f3 !important;
    }

    &.MuiButtonBase-root:disabled {
        background-color: transparent !important;
    }
`;

const StyledSelect = styled(Select)`
    padding: 0;
    color: ${theme.colors.secondary[700]};
    cursor: pointer;

    fieldset {
        border: none;
    }

    .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input {
        padding: 0;
    }

    .MuiSvgIcon-root {
        color: ${theme.colors.secondary[900]};
    }
` as unknown as typeof Select;

const StyledMenuItem = styled(MenuItem)`
    margin: 6px 8px;
    padding: 0 12px;
    border-radius: 8px;
    gap: 6px;
    align-items: center;

    &.Mui-selected,
    &.Mui-selected.Mui-focusVisible {
        background-color: rgba(0, 0, 0, 0.04);

        :hover {
            background-color: rgba(0, 0, 0, 0.04);
        }
    }

    img,
    svg {
        margin-left: -4px;
    }
`;

const AddVoiceButton = () => <TransparentButton startIcon={<MaskedIcon src={Plus} size="14px" />}>Add voice</TransparentButton>;

interface Props {
    setMode: (mode: 'select' | 'import' | 'clone-voice') => void;
}

export const ModalViewChange = ({ setMode }: Props) => {
    const canUseProVoices = useCanUseProVoices();
    const popperRef = useRef<Instance>(null);

    const handleImportMode = () => {
        setMode('import');
    };

    const handleCloneMode = () => {
        mixpanel?.track('clone-voice', { event: 'click', from: 'studio-voice-select' });
        setMode('clone-voice');
    };

    const handleImportVoiceUpgradeClick = () => {
        mixpanel?.track('upgrade-package', { event: 'click', from: 'import-voice' });
    };

    return (
        <>
            <StyledSelect
                value={1}
                renderValue={AddVoiceButton}
                IconComponent={null as any}
                MenuProps={{
                    PaperProps: { sx: { borderRadius: '12px !important' } },
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: -8,
                        horizontal: 0,
                    },
                }}>
                <StyledMenuItem key="clone" onClick={handleCloneMode}>
                    <MaskedIcon src={CloneVoice} size="16px" />
                    Clone your voice
                </StyledMenuItem>

                <Tooltip
                    title={
                        <UpgradeNeededTooltipContent
                            text="Premium voices are available on our Pro plan"
                            onUpgradeClick={handleImportVoiceUpgradeClick}
                        />
                    }
                    placement="top"
                    disableHoverListener={!!canUseProVoices}
                    slotProps={styles}
                    PopperProps={{ popperRef }}>
                    <div>
                        <StyledMenuItem key="import" onClick={handleImportMode} disabled={!canUseProVoices}>
                            <MaskedIcon src={Import} size="16px" />
                            Import voice
                        </StyledMenuItem>
                    </div>
                </Tooltip>
            </StyledSelect>
        </>
    );
};
